/**
 * Interviewer field — single source of truth for every concern on the
 * Details step's interviewer select. See INTERVIEWER_FIELD.md in the
 * ScheduleInterview root for the full behaviour spec.
 *
 * Responsibilities:
 *   1. Fetch `/ats_interviews/interviewers` via TanStack Query for
 *      shared cache, dedupe, and per-week keying.
 *   2. Drive the async select's `loadOptions` through React Query
 *      (`fetchInterviewersListViaCache`) so every dropdown open hits
 *      a warm cache when the user has already typed / viewed.
 *   3. Prefetch the next/previous visible weeks as the calendar
 *      navigates — on first open the list is already hydrated.
 *   4. Render the group header "Interviewers | Interview load
 *      (<range>)" inside MenuList via `componentsOverride.GroupHeading`
 *      so it scrolls with the options (legacy parity; a sticky
 *      `menuHeader` felt "stuck" to recruiters).
 *   5. Single-chip (renderTriggerValue) + multi-chip (renderTag)
 *      render: avatar + name + timezone clock + hover-icon for
 *      "Calendar not connected".
 *   6. Pastel colour chips via `customStyles.multiValue` /
 *      `multiValueLabel`, keyed on the shared `interviewerPastelMap`
 *      so the colour matches the calendar's busy events.
 */
import { useCallback, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Avatar, Text as KulaText } from '@kula-ai/components';
import { XClose } from '@kula-ai/icons-core';
import { InterviewerTimezoneTrigger } from '../timezoneModal';
import {
  resolveInterviewerTimezone,
  useInterviewerTimezoneOverrides,
} from '@/api/interviewerTimezone';
import {
  components as rsComponents,
  type GroupHeadingProps,
  type GroupBase,
} from 'react-select';
import { useQueryClient } from '@tanstack/react-query';
import { useAccountUsersFieldConfig } from '@/formConfigs';
import type { AccountUser } from '@/api/useAccountUsers';
import {
  fetchInterviewersListViaCache,
  prefetchInterviewersList,
  type ListedInterviewer,
} from '@/api/interviewersList';
import { formatTimezoneAbbreviationForDisplay } from '@/utils/formatTimezoneAbbreviation';
import {
  getScheduleInterviewerPastelKey,
  pastelNumberFromKey,
  syncScheduleInterviewerPastelKeys,
} from '../../../state/interviewerPastelMap';
import { cacheInterviewerOptions } from '../../../state/interviewerOptionCache';
import {
  renderInterviewerOptionCell,
  InterviewerTriggerChip,
  InterviewerCalNotConnected,
  InterviewerOutlookOwner,
} from '../fields/interviewer';

/** Synthetic group label for the full/remaining interviewer list. */
const INTERVIEWER_GROUP_LABEL = '__interviewer_list__';
/** Synthetic group label for the plan's pool (pre-configured) interviewers. */
const POOL_GROUP_LABEL = '__pool_selected__';

type InterviewerOption = {
  value: string;
  label: string;
  image?: string;
  // Zoom hosting fields surfaced verbatim from the BE — read by the Host
  // field's renderers + auto-selection logic so the picked interviewer's
  // zoom validity can be evaluated without a refetch.
  zoomStatus?: {
    can_host_zoom_meeting?: boolean;
    zoom_rate_limit_reached?: boolean;
    zoom_plan?: number;
    zoom_plan_type?: string;
  };
  data: {
    timezone?: string;
    week_interview_hours?: number;
    week_interview_hours_limit?: number | null;
  };
};

export interface UseInterviewerFieldOptions {
  label: string;
  placeholder: string;
  isMulti: boolean;
  /** Pre-hydrated options so the async select can resolve pre-filled
   *  value → chip (edit mode) without a fetch-by-id. `data.timezone`
   *  must be supplied so the chip clock renders before the loader
   *  runs. */
  defaultOptions?: Array<{
    value: string;
    label: string;
    description?: string;
    image?: string;
    data?: { timezone?: string };
  }>;
  /** ISO start of the booked slot — anchors the timezone clock on
   *  each selected chip. */
  calendarBookedStartIso?: string | null;
  /** IANA tz used to derive `week_start_date` — Sunday 00:00 in this
   *  zone, YYYY-MM-DD. */
  scheduleTimezone?: string;
  /** Pool-priority hint; omit for adhoc (no plan). */
  atsJobStageActionId?: number;
  /** ISO start of the calendar week the user is currently viewing —
   *  drives the workload + header range and per-week cache buckets. */
  visibleWeekStartIso?: string | null;
  /** IDs whose calendar isn't connected (from busy-events). Each
   *  selected chip renders a warning icon with a hover tooltip. */
  disconnectedInterviewerIds?: number[];
  /**
   * When true, interviewers are split into two groups in the dropdown:
   *   1. "SELECTED" — the plan's pool interviewers (pre-configured)
   *   2. "INTERVIEWERS" — everyone else
   *
   * Activated only for plan-backed one-on-one manual interviews where the
   * plan has a pool of candidate interviewers to pick from.
   */
  showPoolGroup?: boolean;
  /** Called when a chip's globe icon → "Set timezone" button is clicked.
   *  DetailsForm opens the timezone modal in response. */
  onSetTimezone?: (_interviewer: {
    id: number;
    name: string;
    avatar?: string;
  }) => void;
  /** When false, the globe icon is suppressed (legacy parity — only
   *  shown for self-schedule modes). */
  showSetTimezone?: boolean;
  /** Current logged-in user's id — globe is hidden for this user since
   *  they own their own timezone (legacy parity: `!isCalendarOwner`). */
  currentUserId?: number;
  /** Outlook only: user_id of the picked shared-calendar's owner, when
   *  the calendar isn't the recruiter's primary. Chips matching this
   *  id render the "calendar owner won't get an invite" warning —
   *  mirrors legacy `SchInterviewerMultiValueLabel.isCalendarOwner`. */
  outlookCalendarOwnerUserId?: number | null;
}

export function useInterviewerField({
  label,
  placeholder,
  isMulti,
  defaultOptions,
  calendarBookedStartIso,
  scheduleTimezone,
  atsJobStageActionId,
  visibleWeekStartIso,
  disconnectedInterviewerIds,
  showPoolGroup = false,
  onSetTimezone,
  showSetTimezone = false,
  currentUserId,
  outlookCalendarOwnerUserId = null,
}: UseInterviewerFieldOptions) {
  const queryClient = useQueryClient();

  // /account/users/search mapper — only used as a fallback resolver
  // when a pre-filled value needs to be rendered and our loader hasn't
  // run yet. `useAccountUsersFieldConfig` keeps it on the field config
  // so react-select's value → option path always finds a match.
  const optionMapper = useCallback(
    (user: AccountUser) => ({
      value: String(user.id),
      label: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
      description: user.email,
      image: user.avatar_thumb_url,
      data: { timezone: user.timezone ?? '' },
    }),
    []
  );

  const resolvedTz =
    scheduleTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Backend contract: `/ats_interviews/interviewers` rejects any
  // `week_start_date` whose day-name isn't "Sunday" (err_invalid_
  // _week_start_date). The calendar already anchors on Sunday via
  // `startOf('week')`, but we still rewind defensively in case the
  // caller passes any other day-of-week — `subtract(anchor.day(), 'days')`
  // is idempotent when anchor is already Sunday.
  const weekStartDate = useMemo(() => {
    const anchor = visibleWeekStartIso
      ? moment.tz(visibleWeekStartIso, resolvedTz)
      : moment.tz(resolvedTz);
    return anchor.clone().subtract(anchor.day(), 'days').format('YYYY-MM-DD');
  }, [resolvedTz, visibleWeekStartIso]);

  const weekRangeLabel = useMemo(() => {
    const start = moment.tz(weekStartDate, resolvedTz);
    const end = start.clone().add(6, 'days');
    const sameMonth = start.month() === end.month();
    const sameYear = start.year() === end.year();
    if (sameMonth && sameYear) {
      return `${start.format('MMM D')} – ${end.format('D, YYYY')}`;
    }
    if (sameYear) {
      return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
    }
    return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
  }, [weekStartDate, resolvedTz]);

  // Warm React Query cache on week/stage-action change with an empty-
  // query fetch. First dropdown open then resolves synchronously.
  useEffect(() => {
    if (!weekStartDate) return;
    prefetchInterviewersList(queryClient, {
      query: '',
      weekStartDate,
      ...(typeof atsJobStageActionId === 'number' && atsJobStageActionId > 0
        ? { atsJobStageActionId }
        : {}),
    });
  }, [queryClient, weekStartDate, atsJobStageActionId]);

  // Seed the shared pastel map with the hydrated interviewer IDs so the
  // form's chips and the calendar's busy events agree on the first
  // paint. Done during render (useMemo) — a useEffect fires AFTER
  // commit, which means the first chip paint would read an empty map
  // and every chip would fall through to the pastel-1 fallback.
  useMemo(() => {
    if (!defaultOptions?.length) return;
    const ids = defaultOptions
      .map((o) => Number(o.value))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length > 0) {
      syncScheduleInterviewerPastelKeys(ids);
    }
  }, [defaultOptions]);

  // Map a backend row to a react-select option, keeping workload +
  // timezone on `data` for the custom renderers.
  const toOption = useCallback(
    (u: ListedInterviewer): InterviewerOption => ({
      value: String(u.id),
      label: u.name,
      image: u.avatar,
      zoomStatus: {
        can_host_zoom_meeting: u.can_host_zoom_meeting,
        zoom_rate_limit_reached: u.zoom_rate_limit_reached,
        zoom_plan: u.zoom_plan,
        zoom_plan_type: u.zoom_plan_type,
      },
      data: {
        timezone: u.time_zone ?? '',
        week_interview_hours: u.week_interview_hours,
        week_interview_hours_limit: u.week_interview_hours_limit ?? null,
      },
    }),
    []
  );

  // Async loader — flows through React Query so repeated opens with
  // the same (weekStartDate, query, stageActionId) hit the cache.
  //
  // When `showPoolGroup` is true and the plan has pool interviewers, returns
  // TWO groups so the dropdown separates plan-configured candidates from the
  // general user list. Otherwise returns a single flat group (default).
  const loadOptions = useCallback(
    async (
      query: string
    ): Promise<Array<{ label: string; options: InterviewerOption[] }>> => {
      const result = await fetchInterviewersListViaCache(queryClient, {
        query,
        weekStartDate,
        ...(typeof atsJobStageActionId === 'number' && atsJobStageActionId > 0
          ? { atsJobStageActionId }
          : {}),
      });
      const poolOptions = result.pool.map(toOption);
      const otherOptions = result.other.map(toOption);
      cacheInterviewerOptions([...poolOptions, ...otherOptions]);

      if (showPoolGroup && poolOptions.length > 0) {
        return [
          { label: POOL_GROUP_LABEL, options: poolOptions },
          { label: INTERVIEWER_GROUP_LABEL, options: otherOptions },
        ];
      }
      return [
        {
          label: INTERVIEWER_GROUP_LABEL,
          options: [...poolOptions, ...otherOptions],
        },
      ];
    },
    [atsJobStageActionId, queryClient, toOption, weekStartDate, showPoolGroup]
  );

  // Custom GroupHeading — renders inside the scrollable MenuList so the
  // header scrolls with the options (legacy parity).
  //
  // When pool grouping is active, two headings are rendered:
  //   POOL_GROUP_LABEL    → "SELECTED | Interview load (<range>)"
  //   INTERVIEWER_GROUP_LABEL → "INTERVIEWERS" (no range repeat)
  // In single-group mode only INTERVIEWER_GROUP_LABEL renders.
  const CustomGroupHeading = useMemo(() => {
    type Group = { label?: string };
    const Heading = (
      props: GroupHeadingProps<unknown, boolean, GroupBase<unknown>>
    ) => {
      const data = props.data as Group;

      if (data?.label === POOL_GROUP_LABEL) {
        return (
          <rsComponents.GroupHeading {...props}>
            <Flex align="center" justify="space-between" w="100%" px="1" py="1">
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="secondary-700"
                textTransform="uppercase"
                letterSpacing="0.06em"
              >
                Selected
              </Text>
              <Text fontSize="xs" fontWeight="medium" color="secondary-700">
                Interview load ({weekRangeLabel})
              </Text>
            </Flex>
          </rsComponents.GroupHeading>
        );
      }

      if (data?.label === INTERVIEWER_GROUP_LABEL) {
        return (
          <rsComponents.GroupHeading {...props}>
            {/* Divider sits at the OUTER TOP edge of the GroupHeading.
                kula-ai gives the GroupHeading div `padding: 8px 12px`
                (OverallEvaluation-M_EkREdL.js:3682). We negate that
                with `mt="-2"` (cancel 8px padding-top) and `mx="-3"`
                (cancel 12px horizontal padding) so the 1px Box lands
                exactly between the previous group's last option and
                this group's heading, spanning the full menu width. */}
            {showPoolGroup && (
              <Box h="1px" bg="secondary-100" mt="-2" mx="-3" mb="2" />
            )}
            <Flex align="center" justify="space-between" w="100%" px="1" py="1">
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="secondary-700"
                textTransform="uppercase"
                letterSpacing="0.06em"
              >
                Interviewers
              </Text>
              {/* Show load range only for the single-group layout;
                  in pool-group layout it is already in the SELECTED header. */}
              {!showPoolGroup && (
                <Text fontSize="xs" fontWeight="medium" color="secondary-700">
                  Interview load ({weekRangeLabel})
                </Text>
              )}
            </Flex>
          </rsComponents.GroupHeading>
        );
      }

      return <rsComponents.GroupHeading {...props} />;
    };
    Heading.displayName = 'InterviewerGroupHeading';
    return Heading;
  }, [weekRangeLabel, showPoolGroup]);

  // `renderTag` (below) draws the chip body itself — pastel bg +
  // border + avatar + label + remove button, so react-select's
  // default MultiValue wrapper would double up on padding and give
  // the chip two backgrounds. Strip the wrapper's paint and padding
  // so only our renderTag JSX shows. We also use this pass as the
  // "live" pastel sync: every time a chip is about to paint we
  // re-seed the shared map with the currently-selected IDs so adds
  // and removes stay in lock-step with the calendar's busy events
  // (the initial paint is covered by the hook-level `useMemo`).
  // Fast O(1) "is this id disconnected?" lookup for renderers.
  const disconnectedSet = useMemo(
    () => new Set<string>((disconnectedInterviewerIds ?? []).map(String)),
    [disconnectedInterviewerIds]
  );

  const customStyles = useMemo(
    () => ({
      // Single-select: when the lone selected interviewer's calendar is
      // disconnected, paint the whole control red at rest (multi-select
      // shows the error per-tag instead). Mirrors the kula Select's
      // built-in `hasError` treatment (full `border` shorthand + pastel
      // fill on hover/focus), and the block in `useDetailsStepSave`. Use
      // the `border` shorthand — `borderColor` alone gets overridden at
      // rest and only bites on hover.
      control: (
        provided: Record<string, unknown>,
        state: { selectProps?: { value?: unknown } }
      ) => {
        if (isMulti) return provided;
        const v = state.selectProps?.value as { value?: string } | null;
        const opt = Array.isArray(v) ? v[0] : v;
        const isErr = opt?.value
          ? disconnectedSet.has(String(opt.value))
          : false;
        if (!isErr) return provided;
        const red = 'var(--kula-colors-error-200)';
        const fill = 'var(--kula-colors-pastel-1-light)';
        return {
          ...provided,
          border: `1px solid ${red}`,
          boxShadow: 'none',
          '&:hover': { borderColor: red, backgroundColor: fill },
          '&:focus-within': {
            borderColor: red,
            backgroundColor: fill,
            boxShadow: 'none',
          },
        };
      },
      multiValue: (
        provided: Record<string, unknown>,
        state: {
          selectProps: { value?: Array<{ value?: string }> };
        }
      ) => {
        const selected = Array.isArray(state.selectProps?.value)
          ? state.selectProps.value
          : [];
        const ids = selected
          .map((o) => Number(o?.value))
          .filter((id) => Number.isFinite(id) && id > 0);
        if (ids.length > 0) syncScheduleInterviewerPastelKeys(ids);
        return {
          ...provided,
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          margin: '2px 4px 2px 0',
        };
      },
      multiValueLabel: (provided: Record<string, unknown>) => ({
        ...provided,
        padding: 0,
        paddingLeft: 0,
      }),
      multiValueRemove: () => ({ display: 'none' }),
      // Darken the "Selected" / "Interviewers" group headers one step up
      // from kula's default `secondary-25` so the sections read clearly
      // (secondary-100 was too lavender/heavy).
      groupHeading: (provided: Record<string, unknown>) => ({
        ...provided,
        backgroundColor: 'var(--kula-colors-secondary-50)',
      }),
    }),
    [isMulti, disconnectedSet]
  );

  // Option row: avatar + name + workload tag. No timezone clock here
  // — legacy keeps that to the selected chip.
  const renderOption = useCallback(
    (
      option: {
        label: string;
        description?: string;
        image?: string;
        data?: Record<string, unknown>;
      },
      state?: { isSelected: boolean; isFocused: boolean }
    ) => {
      const data = option.data as
        | {
            week_interview_hours?: number;
            week_interview_hours_limit?: number | null;
          }
        | undefined;
      return renderInterviewerOptionCell({
        label: option.label,
        image: option.image,
        workloadHours: data?.week_interview_hours,
        workloadLimitHours: data?.week_interview_hours_limit,
        // kula drops its default checkmark once `renderOption` is set —
        // re-add it for parity with the plain Select options.
        isSelected: state?.isSelected,
      });
    },
    []
  );

  // Bumps whenever a timezone is set from the modal, so both renderers below
  // repaint without a refetch or a remount.
  const timezoneOverrideVersion = useInterviewerTimezoneOverrides();

  // Formatted "10:30AM PST" anchored to the booked slot (or "now" in
  // the interviewer's tz when no slot is picked yet). Empty string
  // when the tz is missing — caller hides the clock cleanly.
  const formatClockFor = useCallback(
    (tz?: string): string => {
      if (!tz || !moment.tz.zone(tz)) return '';
      const anchor = calendarBookedStartIso
        ? moment.tz(calendarBookedStartIso, tz)
        : moment().tz(tz);
      return `${anchor.format('h:mmA')} ${formatTimezoneAbbreviationForDisplay(anchor, tz)}`;
    },
    [calendarBookedStartIso]
  );

  // Single-select trigger chip: avatar + name + clock, icon prepended
  // when the calendar isn't connected.
  const renderTriggerValue = useCallback(
    (selected: unknown) => {
      const opt = Array.isArray(selected) ? selected[0] : selected;
      if (!opt || typeof opt !== 'object') return null;
      const o = opt as {
        value?: string;
        label?: string;
        image?: string;
        data?: { timezone?: string };
      };
      const idNum = Number(o.value);
      // A timezone set from the modal this session wins over the option's own
      // value: the option was snapshotted at selection time, so an ad-hoc pick
      // would otherwise keep reading the empty timezone it was chosen with.
      const clock = formatClockFor(
        resolveInterviewerTimezone(idNum, o.data?.timezone)
      );
      const calendarNotConnected = o.value
        ? disconnectedSet.has(String(o.value))
        : false;
      const showGlobe =
        showSetTimezone &&
        !clock &&
        Number.isFinite(idNum) &&
        idNum !== currentUserId;
      const isOutlookOwner =
        outlookCalendarOwnerUserId !== null &&
        outlookCalendarOwnerUserId !== undefined &&
        Number.isFinite(idNum) &&
        idNum === outlookCalendarOwnerUserId;
      return (
        <InterviewerTriggerChip
          label={o.label ?? ''}
          image={o.image}
          clock={clock}
          calendarNotConnected={calendarNotConnected}
          isOutlookOwner={isOutlookOwner}
          onSetTimezone={
            showGlobe
              ? () =>
                  onSetTimezone?.({
                    id: idNum,
                    name: o.label ?? '',
                    avatar: o.image,
                  })
              : undefined
          }
        />
      );
    },
    // `timezoneOverrideVersion` is not read in the callback — it is listed
    // so a timezone set from the modal repaints the chip. Same idiom as
    // DetailsForm's tzVersion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formatClockFor,
      disconnectedSet,
      onSetTimezone,
      showSetTimezone,
      currentUserId,
      outlookCalendarOwnerUserId,
      timezoneOverrideVersion,
    ]
  );

  // Multi-select chip — pastel background only (matches the calendar's
  // busy event for this interviewer). Text + border stay at default
  // so the chip doesn't feel heavy. Includes avatar, label, timezone
  // clock and a remove ✕.
  const renderTag = useCallback(
    (
      option: {
        value?: string;
        label?: string;
        image?: string;
        data?: { timezone?: string };
      },
      api?: { onRemove?: () => void }
    ) => {
      const idNum = Number(option.value);
      const n = pastelNumberFromKey(getScheduleInterviewerPastelKey(idNum));
      const isDisconnected = option.value
        ? disconnectedSet.has(String(option.value))
        : false;
      // See renderTriggerValue: a timezone set this session beats the
      // option's snapshotted value.
      const clock = formatClockFor(
        resolveInterviewerTimezone(idNum, option.data?.timezone)
      );
      const isOutlookOwner =
        outlookCalendarOwnerUserId !== null &&
        outlookCalendarOwnerUserId !== undefined &&
        Number.isFinite(idNum) &&
        idNum === outlookCalendarOwnerUserId;
      return (
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          gap="1.5"
          minW={0}
          borderRadius="md"
          // Figma: 6px 12px padding → normal chip is 32px tall; the error
          // chip's 24px calendar-close icon pushes it to 36px on its own.
          px="3"
          py="1.5"
          fontSize="sm"
          // Calendar-not-connected → error-state tag (Figma): red ring +
          // light-red fill, name/icons in error-200. Ring via inset
          // box-shadow (not `border`) so the 1px doesn't add to the height.
          boxShadow={
            isDisconnected
              ? 'inset 0 0 0 1px var(--kula-colors-error-200)'
              : undefined
          }
          style={{
            backgroundColor: isDisconnected
              ? 'var(--kula-colors-pastel-1-light)'
              : `var(--kula-colors-pastel-${n}-light)`,
          }}
        >
          {isOutlookOwner && <InterviewerOutlookOwner size={14} />}
          {!isOutlookOwner &&
          showSetTimezone &&
          !clock &&
          Number.isFinite(idNum) &&
          idNum !== currentUserId ? (
            <InterviewerTimezoneTrigger
              onSetTimezone={() =>
                onSetTimezone?.({
                  id: idNum,
                  name: option.label ?? '',
                  avatar: option.image,
                })
              }
            />
          ) : null}
          {isDisconnected && !isOutlookOwner && (
            <InterviewerCalNotConnected
              size={20}
              color="var(--kula-colors-error-200)"
            />
          )}
          <Avatar
            src={option.image}
            name={option.label ?? ''}
            size="xs"
            shape="square"
          />
          {/* isTruncated (not noOfLines): the -webkit-box clamp reserves
              the full untruncated name width as flex-basis, leaving dead
              space between the ellipsis and the clock. */}
          <KulaText
            as="span"
            variant="p"
            color={isDisconnected ? 'error' : 'body'}
            truncate
            // lineHeight 20px (not variant `p`'s 21px) so the text box
            // matches the 20px avatar/icons and centers cleanly.
            style={{ minWidth: 0, maxWidth: '180px', lineHeight: '20px' }}
          >
            {option.label ?? ''}
          </KulaText>
          {clock ? (
            <KulaText
              as="span"
              variant="caption"
              color={isDisconnected ? 'error' : 'muted'}
              style={{
                whiteSpace: 'nowrap',
                flexShrink: 0,
                lineHeight: '20px',
                marginTop: '2px',
              }}
            >
              {clock}
            </KulaText>
          ) : null}
          {api?.onRemove ? (
            <Box
              as="button"
              type="button"
              tabIndex={-1}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                api.onRemove?.();
              }}
              display="inline-flex"
              alignItems="center"
              opacity={0.7}
              _hover={{ opacity: 1 }}
              color={isDisconnected ? 'error-200' : 'secondary-700'}
              aria-label={`Remove ${option.label ?? ''}`}
            >
              <XClose size={18} />
            </Box>
          ) : null}
        </Box>
      );
    },
    // `timezoneOverrideVersion` is not read in the callback — it is listed
    // so a timezone set from the modal repaints the chip. Same idiom as
    // DetailsForm's tzVersion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      disconnectedSet,
      formatClockFor,
      onSetTimezone,
      showSetTimezone,
      currentUserId,
      outlookCalendarOwnerUserId,
      timezoneOverrideVersion,
    ]
  );

  const { fieldConfig: base } = useAccountUsersFieldConfig({
    name: 'participant_ids',
    label,
    isMulti,
    placeholder,
    customOptionMapper: optionMapper,
  });

  return useMemo(() => {
    const fp = base.fieldProps as Record<string, unknown> | undefined;
    return {
      ...base,
      // Top-level options carry pre-hydrated selected interviewers so
      // react-select's value → chip resolver always succeeds in edit
      // mode, even before `loadOptions` runs.
      ...(defaultOptions?.length ? { options: defaultOptions } : {}),
      fieldProps: {
        ...fp,
        loadOptions,
        customStyles,
        renderOption,
        renderTriggerValue,
        renderTag,
        // Inject the GroupHeading override — this is the only slot
        // that renders inside MenuList *and* has per-group context.
        componentsOverride: {
          ...((fp?.componentsOverride as Record<string, unknown>) ?? {}),
          GroupHeading: CustomGroupHeading,
        },
      },
    };
  }, [
    CustomGroupHeading,
    base,
    customStyles,
    defaultOptions,
    loadOptions,
    renderOption,
    renderTag,
    renderTriggerValue,
  ]);
}
